'use client';

import { useState, useRef, useEffect } from 'react';
import {
  Container,
  Title,
  Card,
  Stack,
  Group,
  Button,
  TextInput,
  Text,
  Badge,
  Loader,
  ActionIcon,
  Paper,
  ScrollArea,
  Grid,
  Progress,
  Tooltip
} from '@mantine/core';
import {
  IconMicrophone,
  IconMicrophoneOff,
  IconSend,
  IconVolume,
  IconVolumeOff,
  IconRobot,
  IconActivity,
  IconCpu,
  IconDatabase,
  IconGitBranch,
  IconCheck,
  IconX,
  IconClock
} from '@tabler/icons-react';
import { showSuccess, showError, showLoading } from '@/lib/toast';

// Simple logger for client-side
const logger = {
  info: (...args: any[]) => console.log('[System Control]', ...args),
  error: (...args: any[]) => console.error('[System Control]', ...args)
};

interface SystemMessage {
  id: string;
  type: 'user' | 'system';
  content: string;
  timestamp: Date;
  action?: string;
  result?: any;
  suggestions?: string[];
}

interface SystemStatus {
  health: string;
  projects: { total: number };
  plans: { total: number };
  tasks: {
    total: number;
    pending: number;
    inProgress: number;
    completed: number;
    failed: number;
    successRate: string;
  };
  workers: {
    active: number;
    list: any[];
  };
  queue: {
    pending: number;
    processing: number;
    failed: number;
    total: number;
  };
  uptime: string;
}

export default function SystemControlPage() {
  const [messages, setMessages] = useState<SystemMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [recording, setRecording] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [status, setStatus] = useState<SystemStatus | null>(null);
  
  // Web Speech API references
  const recognitionRef = useRef<any>(null);
  const speechSynthesisRef = useRef<SpeechSynthesisUtterance | null>(null);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  // Load system status
  useEffect(() => {
    loadSystemStatus();
    const interval = setInterval(loadSystemStatus, 10000); // Refresh every 10s
    return () => clearInterval(interval);
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Initialize Web Speech API
  useEffect(() => {
    // Check if browser supports Web Speech API
    if (typeof window !== 'undefined') {
      const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
      
      if (SpeechRecognition) {
        const recognition = new SpeechRecognition();
        recognition.continuous = false;
        recognition.interimResults = false;
        recognition.lang = 'vi-VN'; // Vietnamese - can switch to 'en-US'
        
        recognition.onresult = (event: any) => {
          const transcript = event.results[0][0].transcript;
          handleVoiceResult(transcript);
        };
        
        recognition.onerror = (event: any) => {
          console.error('Speech recognition error:', event.error);
          setRecording(false);
          showError(`Lỗi nhận dạng giọng nói: ${event.error}`);
        };
        
        recognition.onend = () => {
          setRecording(false);
        };
        
        recognitionRef.current = recognition;
      } else {
        console.warn('Web Speech API not supported in this browser');
      }
    }
  }, []);

  async function loadSystemStatus() {
    try {
      const response = await fetch('/api/system/status');
      const data = await response.json();
      if (data.success) {
        setStatus(data.status);
      }
    } catch (error) {
      console.error('Failed to load system status:', error);
    }
  }

  async function sendCommand(text: string, isVoice: boolean = false) {
    if (!text.trim()) return;

    const userMessage: SystemMessage = {
      id: Date.now().toString(),
      type: 'user',
      content: text,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setLoading(true);

    try {
      const response = await fetch('/api/system/command', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          input: text,
          type: isVoice ? 'voice' : 'text'
        })
      });

      const data = await response.json();

      const systemMessage: SystemMessage = {
        id: (Date.now() + 1).toString(),
        type: 'system',
        content: data.message || 'Command executed',
        timestamp: new Date(),
        action: data.action,
        result: data.result,
        suggestions: data.suggestions
      };

      setMessages(prev => [...prev, systemMessage]);

      // Speak response if voice enabled
      if (voiceEnabled && data.message) {
        speakText(data.message);
      }

      // Show toast notification
      if (data.success) {
        showSuccess(data.message);
      } else {
        showError(data.message);
      }

      // Refresh status after command
      loadSystemStatus();
    } catch (error: any) {
      showError(`Error: ${error.message}`);
      
      const errorMessage: SystemMessage = {
        id: (Date.now() + 1).toString(),
        type: 'system',
        content: `Error: ${error.message}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setLoading(false);
    }
  }

  async function startRecording() {
    if (!recognitionRef.current) {
      showError('Trình duyệt không hỗ trợ nhận dạng giọng nói');
      return;
    }

    try {
      recognitionRef.current.start();
      setRecording(true);
      showLoading('Đang lắng nghe... Hãy nói lệnh của bạn');
    } catch (error: any) {
      if (error.message.includes('already started')) {
        // Already recording, ignore
        return;
      }
      showError(`Không thể khởi động nhận dạng giọng nói: ${error.message}`);
    }
  }

  function stopRecording() {
    if (recognitionRef.current && recording) {
      recognitionRef.current.stop();
      setRecording(false);
    }
  }

  async function handleVoiceResult(transcript: string) {
    setLoading(true);

    try {
      logger.info('[Voice] Transcribed:', transcript);

      // Show transcribed text as user message
      const userMessage: SystemMessage = {
        id: Date.now().toString(),
        type: 'user',
        content: `🎤 ${transcript}`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, userMessage]);

      // Send to API
      const response = await fetch('/api/system/voice', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          transcribedText: transcript
        })
      });

      const data = await response.json();

      // Show system response
      const systemMessage: SystemMessage = {
        id: (Date.now() + 1).toString(),
        type: 'system',
        content: data.message || 'Command executed',
        timestamp: new Date(),
        action: data.action,
        result: data.result,
        suggestions: data.suggestions
      };
      setMessages(prev => [...prev, systemMessage]);

      // Speak response if voice enabled
      if (voiceEnabled && data.message) {
        speakText(data.message);
      }

      showSuccess('Đã thực hiện lệnh');
      loadSystemStatus();
    } catch (error: any) {
      showError(`Lỗi xử lý lệnh: ${error.message}`);
    } finally {
      setLoading(false);
    }
  }

  function speakText(text: string) {
    if (typeof window === 'undefined' || !window.speechSynthesis) {
      console.warn('Speech synthesis not supported');
      return;
    }

    // Cancel any ongoing speech
    window.speechSynthesis.cancel();

    const utterance = new SpeechSynthesisUtterance(text);
    utterance.lang = 'vi-VN'; // Vietnamese
    utterance.rate = 1.0;
    utterance.pitch = 1.0;
    utterance.volume = 1.0;

    window.speechSynthesis.speak(utterance);
    speechSynthesisRef.current = utterance;
  }

  function handleSuggestionClick(suggestion: string) {
    setInput(suggestion);
  }

  const getHealthColor = (health: string) => {
    switch (health) {
      case 'healthy': return 'green';
      case 'warning': return 'yellow';
      case 'degraded': return 'orange';
      case 'critical': return 'red';
      default: return 'gray';
    }
  };

  return (
    <Container size="xl" py="xl">
      <Stack gap="lg">
        {/* Header */}
        <Group justify="space-between">
          <Group>
            <IconRobot size={32} />
            <div>
              <Title order={2}>System Orchestrator</Title>
              <Text size="sm" c="dimmed">AI điều khiển tổng toàn hệ thống</Text>
            </div>
          </Group>
          {status && (
            <Badge 
              size="lg" 
              color={getHealthColor(status.health)}
              leftSection={<IconActivity size={16} />}
            >
              {status.health.toUpperCase()}
            </Badge>
          )}
        </Group>

        {/* System Status Dashboard */}
        {status && (
          <Grid>
            <Grid.Col span={{ base: 12, md: 3 }}>
              <Card withBorder padding="md">
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm" fw={500}>Projects</Text>
                    <IconDatabase size={20} />
                  </Group>
                  <Text size="xl" fw={700}>{status.projects.total}</Text>
                </Stack>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 3 }}>
              <Card withBorder padding="md">
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm" fw={500}>Active Workers</Text>
                    <IconCpu size={20} />
                  </Group>
                  <Text size="xl" fw={700}>{status.workers.active}</Text>
                </Stack>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 3 }}>
              <Card withBorder padding="md">
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm" fw={500}>Queue</Text>
                    <IconClock size={20} />
                  </Group>
                  <Text size="xl" fw={700}>{status.queue.pending}</Text>
                  <Text size="xs" c="dimmed">pending jobs</Text>
                </Stack>
              </Card>
            </Grid.Col>

            <Grid.Col span={{ base: 12, md: 3 }}>
              <Card withBorder padding="md">
                <Stack gap="xs">
                  <Group justify="space-between">
                    <Text size="sm" fw={500}>Tasks</Text>
                    <IconCheck size={20} />
                  </Group>
                  <Text size="xl" fw={700}>{status.tasks.total}</Text>
                  <Progress 
                    value={(status.tasks.completed / status.tasks.total) * 100} 
                    size="sm"
                    color="green"
                  />
                  <Text size="xs" c="dimmed">{status.tasks.successRate} success</Text>
                </Stack>
              </Card>
            </Grid.Col>
          </Grid>
        )}

        {/* Chat Interface */}
        <Card withBorder padding="lg">
          <Stack gap="md">
            {/* Messages */}
            <ScrollArea h={400} offsetScrollbars>
              <Stack gap="md">
                {messages.length === 0 && (
                  <Paper p="xl" bg="gray.0" ta="center">
                    <IconRobot size={48} style={{ margin: '0 auto', opacity: 0.5 }} />
                    <Text component="div" mt="md" c="dimmed">
                      Xin chào! Tôi là System Orchestrator AI.
                      <br />
                      Hãy nói hoặc gõ lệnh bằng tiếng Việt để điều khiển hệ thống.
                      <br />
                      <Text component="div" size="xs" mt="xs" c="dimmed">
                        💡 Sử dụng Web Speech API - miễn phí, không cần API key
                      </Text>
                    </Text>
                  </Paper>
                )}

                {messages.map((msg) => (
                  <Paper
                    key={msg.id}
                    p="md"
                    bg={msg.type === 'user' ? 'blue.0' : 'gray.0'}
                    style={{ alignSelf: msg.type === 'user' ? 'flex-end' : 'flex-start', maxWidth: '80%' }}
                  >
                    <Stack gap="xs">
                      <Group justify="space-between">
                        <Text size="sm" fw={500}>
                          {msg.type === 'user' ? 'You' : 'System AI'}
                        </Text>
                        <Text size="xs" c="dimmed">
                          {msg.timestamp.toLocaleTimeString()}
                        </Text>
                      </Group>
                      <Text>{msg.content}</Text>
                      
                      {msg.action && (
                        <Badge variant="light" size="sm">
                          Action: {msg.action}
                        </Badge>
                      )}

                      {msg.suggestions && msg.suggestions.length > 0 && (
                        <Stack gap="xs">
                          <Text size="sm" fw={500}>Suggestions:</Text>
                          {msg.suggestions.map((suggestion, idx) => (
                            <Button
                              key={idx}
                              size="xs"
                              variant="light"
                              onClick={() => handleSuggestionClick(suggestion)}
                            >
                              {suggestion}
                            </Button>
                          ))}
                        </Stack>
                      )}
                    </Stack>
                  </Paper>
                ))}

                {loading && (
                  <Paper p="md" bg="gray.0" style={{ alignSelf: 'flex-start' }}>
                    <Group>
                      <Loader size="sm" />
                      <Text size="sm">Processing...</Text>
                    </Group>
                  </Paper>
                )}

                <div ref={messagesEndRef} />
              </Stack>
            </ScrollArea>

            {/* Input */}
            <Group gap="xs" wrap="nowrap">
              <Tooltip label={voiceEnabled ? 'Voice enabled' : 'Voice disabled'}>
                <ActionIcon
                  variant={voiceEnabled ? 'filled' : 'light'}
                  color={voiceEnabled ? 'blue' : 'gray'}
                  size="lg"
                  onClick={() => setVoiceEnabled(!voiceEnabled)}
                >
                  {voiceEnabled ? <IconVolume size={20} /> : <IconVolumeOff size={20} />}
                </ActionIcon>
              </Tooltip>

              <ActionIcon
                variant={recording ? 'filled' : 'light'}
                color={recording ? 'red' : 'blue'}
                size="lg"
                onClick={recording ? stopRecording : startRecording}
                disabled={loading}
              >
                {recording ? <IconMicrophoneOff size={20} /> : <IconMicrophone size={20} />}
              </ActionIcon>

              <TextInput
                flex={1}
                placeholder="Gõ lệnh hoặc nhấn micro để nói (hỗ trợ tiếng Việt)..."
                value={input}
                onChange={(e) => setInput(e.currentTarget.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendCommand(input)}
                disabled={loading || recording}
              />

              <Button
                onClick={() => sendCommand(input)}
                disabled={loading || recording || !input.trim()}
                leftSection={<IconSend size={16} />}
              >
                Send
              </Button>
            </Group>

            {/* Quick Actions */}
            <Group gap="xs">
              <Text size="sm" c="dimmed">Quick:</Text>
              <Button size="xs" variant="light" onClick={() => setInput('Hiển thị trạng thái hệ thống')}>
                Status
              </Button>
              <Button size="xs" variant="light" onClick={() => setInput('Tạo project mới')}>
                New Project
              </Button>
              <Button size="xs" variant="light" onClick={() => setInput('Xem logs')}>
                View Logs
              </Button>
              <Button size="xs" variant="light" onClick={() => setInput('help')}>
                Help
              </Button>
            </Group>
          </Stack>
        </Card>
      </Stack>
    </Container>
  );
}
